import React from "react";
import { AHText, Box, ListItem } from "arkh";

import { graphql, useFragment } from "react-relay";

import { FormattedMessage } from "react-intl";
import { OrgSearchInput } from "../../OrgSearchInput";
import { WorkspaceTodoStoresItemContainer } from "./WorkspaceTodoStoresItemContainer";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { PillIndicator } from "../../PillIndicator";
import { WorkspaceTodoListHeader_todoWorkspace$key } from "./__generated__/WorkspaceTodoListHeader_todoWorkspace.graphql";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { View } from "react-native";

function SecondRow(props: { children?: React.ReactNode }) {
  return (
    <Box
      spaceTop="small"
      style={{
        flexDirection: "row",
        height: 34,
        alignItems: "center",
      }}
    >
      {props.children}
    </Box>
  );
}

export type WorkspaceTodoOrderByKey =
  | "store"
  | "status"
  | "completedAt"
  | "comments";
export type WorkspaceTodoOrder = {
  column: WorkspaceTodoOrderByKey;
  order: "ASC" | "DESC";
};
export function WorkspaceTodoListHeader(props: {
  setOrderBy: (_: WorkspaceTodoOrder) => void;
  order: WorkspaceTodoOrder;

  search: string;
  setSearch: (search: string) => void;
  workspaceTodo: WorkspaceTodoListHeader_todoWorkspace$key;
}) {
  const workspaceTodo = useFragment(
    graphql`
      fragment WorkspaceTodoListHeader_todoWorkspace on TodoWorkspace {
        id
        todoCount(workspaceId: $workspaceId)
        todosCompletedCount(workspaceId: $workspaceId)
        responseType
      }
    `,
    props.workspaceTodo
  );

  const percentage =
    workspaceTodo.todoCount > 0
      ? (workspaceTodo.todosCompletedCount / workspaceTodo.todoCount) * 100
      : 0;

  const sortableProp = (key: WorkspaceTodoOrderByKey) => ({
    order: props.order.column === key ? props.order.order : null,
    onOrderBySelect: () =>
      props.setOrderBy({
        column: key,
        order: "DESC",
      }),
    onOrderChange: (order: "ASC" | "DESC") =>
      props.setOrderBy({ column: key, order }),
  });

  const intl_tokens = useIntlTokens();
  return (
    <Box color="surface-background-light" spaceTop="medium">
      <OrgSearchInput value={props.search} onChangeText={props.setSearch} />
      <ListItem disabled border="border-primary" displayMode="TABLE">
        <WorkspaceTodoStoresItemContainer
          storeName={
            <>
              <SortableColumnTitle
                {...sortableProp("store")}
                title={intl_tokens.Name}
              />
              <SecondRow>
                <AHText>
                  <FormattedMessage
                    defaultMessage="Total"
                    description="Workspace Todo List Header Total"
                  />
                </AHText>
              </SecondRow>
            </>
          }
          completedAt={
            <>
              <SortableColumnTitle
                {...sortableProp("completedAt")}
                title={
                  <FormattedMessage
                    defaultMessage="Completed"
                    description="WorkspaceTodoListHeader status"
                  />
                }
              />
              <SecondRow />
            </>
          }
          comments={
            <>
              <SortableColumnTitle
                {...sortableProp("comments")}
                title={
                  <FormattedMessage
                    defaultMessage="Comments"
                    description="WorkspaceTodoListHeader comments"
                  />
                }
              />
              <SecondRow />
            </>
          }
          response={
            workspaceTodo.responseType ? (
              <>
                <SortableColumnTitle
                  {...sortableProp("status")}
                  title={
                    <FormattedMessage
                      defaultMessage="Response"
                      description="WorkspaceTodoListHeader response"
                    />
                  }
                />
                <SecondRow />
              </>
            ) : undefined
          }
          status={
            <View
              style={{
                alignItems: "flex-end",
                flex: 1,
              }}
            >
              <SortableColumnTitle
                {...sortableProp("status")}
                title={
                  <FormattedMessage
                    defaultMessage="Status"
                    description="WorkspaceTodoListHeader status"
                  />
                }
              />
              <SecondRow>
                <PillIndicator percentage={percentage} />
              </SecondRow>
            </View>
          }
        />
      </ListItem>
    </Box>
  );
}

export function WorkspaceTodoListHeaderGallery(props: {
  search: string;
  setSearch: (search: string) => void;
}) {
  return (
    <Box color="surface-background-light" spaceTop="medium">
      <OrgSearchInput
        value={props.search}
        onChangeText={props.setSearch}
        mode="standalone"
      />
    </Box>
  );
}
